import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Drawer, DrawerSection, DrawerDetailRow, DrawerFooter } from '@/components/ui/drawer'
import { getMailTemplateById } from '@/api/mail-templates.api'
import type { MailTemplate } from '@/types/api'
import { MediaPreviewModal } from '@/pages/events/MediaPreviewModal'
import type { PreviewFile } from '@/pages/events/MediaPreviewModal'

interface MailTemplateDetailDrawerProps {
    templateId: string | null
    showUnitColumn?: boolean
    onClose: () => void
    onUpdate?: (template: MailTemplate) => void
    onDownload?: (url: string, fileName?: string) => void
}

export function MailTemplateDetailDrawer({
    templateId,
    showUnitColumn = true,
    onClose,
    onUpdate,
    onDownload,
}: MailTemplateDetailDrawerProps) {
    const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null)

    const { data: template, isLoading } = useQuery({
        queryKey: ['mail-template-detail', templateId],
        queryFn: ({ signal }) =>
            templateId ? getMailTemplateById(templateId, signal) : Promise.resolve(null),
        enabled: !!templateId,
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })

    const hasAttachedFiles = !!template?.files?.length

    return (
        <>
            <Drawer
                open={!!templateId}
                onClose={onClose}
                title="CHI TIẾT MẪU MAIL"
                styles={{ body: { display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
                footer={
                    <DrawerFooter>
                        <Button variant="outline" size="md" onClick={onClose} className="min-w-[160px] h-10">
                            Đóng
                        </Button>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() => template && onUpdate?.(template)}
                            className="min-w-[160px] h-10"
                            disabled={!template || isLoading}
                        >
                            Cập nhật
                        </Button>
                    </DrawerFooter>
                }
            >
                {isLoading && (
                    <div className="p-6 text-center text-gray-500">
                        Đang tải chi tiết...
                    </div>
                )}

                {template && (
                    <div className="flex flex-col flex-1 h-full overflow-y-auto gap-4 p-1">
                        <DrawerSection title="Thông tin chung">
                            {showUnitColumn && (
                                <DrawerDetailRow label="Đơn vị:" value={template.unitName || '-'} />
                            )}
                            <DrawerDetailRow label="Tên mẫu:" value={template.name} />
                            <DrawerDetailRow label="Chức năng áp dụng:" value={template.functionName || '-'} />
                            <DrawerDetailRow label="Tiêu đề:" value={template.title} />
                        </DrawerSection>

                        <DrawerSection title="Tệp đính kèm">
                            {hasAttachedFiles ? (
                                <div className="mb-4 flex-1 flex flex-col">
                                    <div className="flex flex-wrap gap-2">
                                        {template.files!.map((file) => (
                                            <Button
                                                key={file.id}
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    setPreviewFile({
                                                        url: file.fileUrl,
                                                        name: file.originFileName || 'attached_file',
                                                    })
                                                }
                                                className="items-center !border-[#CE7A58] !text-[#0E6DD2] hover:!border-[#CE7A58] max-w-full sm:max-w-[200px] h-8 gap-1.5 rounded-full"
                                            >
                                                <span className="truncate hover:underline">
                                                    {file.originFileName || 'attached_file'}
                                                </span>
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-4 text-sm text-gray-400">
                                    Không có tệp đính kèm.
                                </div>
                            )}
                        </DrawerSection>

                        <DrawerSection title="Nội dung mail">
                            <div
                                className="py-2 text-sm prose max-w-none"
                                dangerouslySetInnerHTML={{ __html: template.content || '<p class="text-gray-400">Không có nội dung.</p>' }}
                            />
                        </DrawerSection>
                    </div>
                )}
            </Drawer>

            <MediaPreviewModal
                file={previewFile}
                onClose={() => setPreviewFile(null)}
                onDownload={onDownload}
            />
        </>
    )
}